import { useEffect } from 'react';

let nextDiagram = 0;
let queue = Promise.resolve();

export default function useMermaid(ref, content, theme) {
    useEffect(() => {
        const figures = [...(ref.current?.querySelectorAll('[data-mermaid]') || [])];
        if (!figures.length) return undefined;
        let cancelled = false;
        // Serialize rendering because Mermaid's configuration is shared. This also
        // keeps React StrictMode's cancelled first effect from racing the second.
        queue = queue.catch(() => {}).then(async () => {
            const { default: mermaid } = await import('mermaid');
            if (cancelled) return;
            // The app's font gate already settled; don't wait again on a timed-out
            // download when the page deliberately chose its stable fallback.
            const light = theme === 'light';
            mermaid.initialize({
                startOnLoad: false, securityLevel: 'strict', theme: 'base', look: 'classic',
                fontFamily: document.documentElement.classList.contains('font-fallback') ? 'Arial' : 'Hanken Grotesk',
                themeVariables: {
                    primaryColor: light ? '#e8e5df' : '#0c100e',
                    primaryTextColor: light ? '#231f1a' : '#f4f4f2',
                    primaryBorderColor: light ? '#829087' : '#52665a',
                    lineColor: light ? '#657b6b' : '#708e7c',
                    dropShadow: 'none',
                    secondaryColor: light ? '#e8e5df' : '#13191b',
                    tertiaryColor: light ? '#e8e5df' : '#0b100e',
                    clusterBkg: light ? '#e0e5de' : '#0b100e',
                    clusterBorder: light ? '#789582' : '#375141',
                    edgeLabelBackground: light ? '#e8e5df' : '#060708',
                    fontSize: '16px',
                },
                flowchart: { htmlLabels: false, useMaxWidth: false, curve: 'linear', nodeSpacing: 28, rankSpacing: 40 },
            });
            for (const figure of figures) {
                if (cancelled) return;
                const canvas = figure.querySelector('.blog-diagram-canvas');
                try {
                    const { svg } = await mermaid.render(`homelab-diagram-${nextDiagram++}`, figure.dataset.mermaid);
                    if (cancelled) return;
                    canvas.innerHTML = svg;
                    canvas.setAttribute('aria-busy', 'false');
                    canvas.setAttribute('tabindex', '0');
                    canvas.setAttribute('role', 'region');
                    canvas.setAttribute('aria-label', 'Diagram; scroll horizontally to explore');
                } catch {
                    canvas.textContent = 'This diagram could not be displayed.';
                    canvas.setAttribute('aria-busy', 'false');
                }
            }
        }).catch(() => {
            if (cancelled) return;
            figures.forEach(figure => {
                const canvas = figure.querySelector('.blog-diagram-canvas');
                canvas.textContent = 'This diagram could not be loaded. Please refresh to try again.';
                canvas.setAttribute('aria-busy', 'false');
            });
        });
        return () => { cancelled = true; };
    }, [ref, content, theme]);
}
